import React, { useState, useCallback, useRef, useEffect } from "react";
import { Stack, Box, Alert, Typography } from "@mui/material";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";

import { DashboardHeader } from "../shared";
import LocationSelector from "../Clinical/LocationSelector";
import InfoButton from "../Clinical/InfoButton";
import TaskModal from "../Clinical/TaskModal";

import PanelCMOverview from "./PanelCMOverview";
import PanelTransitions from "./PanelTransitions";
import PanelUnmanagedRisk from "./PanelUnmanagedRisk";
import PanelERUtilization from "./PanelERUtilization";
import PanelOutreach from "./PanelOutreach";
import PanelCMTasks from "./PanelCMTasks";
import PanelPolypharmacy from "./PanelPolypharmacy";

import useCMLocationSelector from "../../hooks/useCMLocationSelector";
import useCareManagerData from "../../hooks/useCareManagerData";
import useTasks from "../../hooks/useTasks";
import useAuthUser from "../../../../hooks/useAuthUser";
import { getEmployees } from "../../../../utils/services/control/masterDataService";
import { TASK_CATEGORIES, TASK_PRIORITIES } from "../../constants/clinicalDashboard";

import { CM_HELP_CONTENT } from "../../constants/cmHelpContent";

/**
 * CareManagerDashboard — Orquestador principal del dashboard de Care Management.
 * Renderiza Header + LocationSelector + 7 paneles + TaskModal.
 */
export default function CareManagerDashboard() {
  const {
    locations,
    selectedLocation,
    setSelectedLocation,
    loading: locationsLoading,
  } = useCMLocationSelector();

  const {
    overview,
    transitions,
    unmanagedRisk,
    erUtilization,
    outreach,
    polypharmacy,
    loading,
    error,
  } = useCareManagerData(selectedLocation?.key);

  const {
    tasks,
    stats: taskStats,
    createTask,
    completeTask,
    updateTask,
    deleteTask,
  } = useTasks(selectedLocation?.locationId || null);

  const currentUser = useAuthUser();
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    let cancelled = false;
    getEmployees({ status: 1 })
      .then((data) => { if (!cancelled) setEmployees(data || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // ── Panel refs for scroll-to ───────────────────────────────────
  const panelRefs = {
    transitions: useRef(null),
    unmanagedRisk: useRef(null),
    erUtilization: useRef(null),
  };

  const scrollToPanel = useCallback((panelKey) => {
    panelRefs[panelKey]?.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Task Modal State ─────────────────────────────────────────
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [taskModalDefaults, setTaskModalDefaults] = useState(null);

  const openTaskModal = useCallback((defaults = null) => {
    setTaskModalDefaults(defaults);
    setTaskModalOpen(true);
  }, []);

  const handleCreateTask = useCallback(
    (taskData) => {
      const categoryId = TASK_CATEGORIES.find((c) => c.key === taskData.category)?.id || TASK_CATEGORIES[8]?.id;
      const priorityId = TASK_PRIORITIES.find((p) => p.key === taskData.priority)?.id || TASK_PRIORITIES[1]?.id;
      createTask({
        ...taskData,
        categoryId,
        priorityId,
        locationId: selectedLocation?.locationId || null,
        creatorId: currentUser?.employeeId || currentUser?.employee || null,
      });
    },
    [createTask, selectedLocation, currentUser]
  );

  // ── Panel CTAs ───────────────────────────────────────────────

  // Panel 2 (Transitions) → follow-up task
  const handleTransitionCreateTask = useCallback(
    (patient) => {
      openTaskModal({
        title: `Follow-up Discharge: ${patient.name}`,
        category: "follow_up",
        priority: "high",
        cipId: patient.cipId,
        patientName: patient.name,
      });
    },
    [openTaskModal]
  );

  // Panel 3 (Unmanaged Risk) → care plan task
  const handleUnmanagedCreateTask = useCallback(
    (patient) => {
      openTaskModal({
        title: `Care Plan: ${patient.name}`,
        category: "care_gap",
        priority: "high",
        cipId: patient.cipId,
        patientName: patient.name,
      });
    },
    [openTaskModal]
  );

  // Panel 4 (ER) → intervention task
  const handleERCreateTask = useCallback(
    (patient) => {
      openTaskModal({
        title: `ER Intervention: ${patient.name}`,
        category: "outreach_call",
        priority: "high",
        cipId: patient.cipId,
        patientName: patient.name,
      });
    },
    [openTaskModal]
  );

  // Panel 5 (Outreach) → log call task
  const handleOutreachCreateTask = useCallback(
    (patient) => {
      openTaskModal({
        title: `Patient Recall: ${patient.name}`,
        category: "patient_recall",
        priority: "medium",
        cipId: patient.cipId,
        patientName: patient.name,
      });
    },
    [openTaskModal]
  );

  // Panel 7 (Polypharmacy) → med reconciliation task
  const handlePolypharmacyCreateTask = useCallback(
    (patient) => {
      openTaskModal({
        title: `Med Reconciliation: ${patient.name}`,
        category: "clinical",
        priority: "medium",
        cipId: patient.cipId,
        patientName: patient.name,
      });
    },
    [openTaskModal]
  );

  // Build patient list for autocomplete in TaskModal
  const patientList = React.useMemo(() => {
    const lists = [
      unmanagedRisk?.patients,
      outreach?.patients,
      transitions?.discharges,
    ];
    const seen = new Set();
    const result = [];
    for (const list of lists) {
      if (!list) continue;
      for (const p of list) {
        if (!seen.has(p.cipId)) {
          seen.add(p.cipId);
          result.push(p);
        }
      }
    }
    return result;
  }, [unmanagedRisk, outreach, transitions]);

  return (
    <Stack spacing={2.5}>
      {/* Header */}
      <DashboardHeader
        title="Care Management Dashboard"
        subtitle="Transition of care, risk management, outreach, and clinical coordination"
        icon={<SupportAgentIcon />}
        color="error"
      />

      {/* Location Selector */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1.5}
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <LocationSelector
            locations={locations}
            selectedLocation={selectedLocation}
            onChangeLocation={setSelectedLocation}
            loading={locationsLoading}
          />
          <InfoButton helpContent={CM_HELP_CONTENT.locationSelector} />
        </Stack>
      </Stack>

      {/* Error */}
      {error && (
        <Alert
          severity="error"
          sx={{ borderRadius: 3, "& .MuiAlert-icon": { alignItems: "center" } }}
        >
          <Typography fontWeight={600}>Something went wrong</Typography>
          <Typography variant="body2">{String(error)}</Typography>
        </Alert>
      )}

      {/* Panel 1: KPI Overview */}
      <PanelCMOverview
        kpis={overview?.kpis}
        loading={loading.overview}
        onScrollToPanel={scrollToPanel}
        helpContent={CM_HELP_CONTENT.cmOverview}
      />

      {/* Panel 2: Transition of Care */}
      <Box ref={panelRefs.transitions}>
        <PanelTransitions
          data={transitions}
          loading={loading.transitions}
          onCreateTask={handleTransitionCreateTask}
          helpContent={CM_HELP_CONTENT.transitions}
        />
      </Box>

      {/* Panel 3: Unmanaged High Risk */}
      <Box ref={panelRefs.unmanagedRisk}>
        <PanelUnmanagedRisk
          data={unmanagedRisk}
          loading={loading.unmanagedRisk}
          onCreateTask={handleUnmanagedCreateTask}
          helpContent={CM_HELP_CONTENT.unmanagedRisk}
        />
      </Box>

      {/* Panel 4: ER & Utilization */}
      <Box ref={panelRefs.erUtilization}>
        <PanelERUtilization
          data={erUtilization}
          loading={loading.erUtilization}
          onCreateTask={handleERCreateTask}
          helpContent={CM_HELP_CONTENT.erUtilization}
        />
      </Box>

      {/* Panel 5: Outreach */}
      <PanelOutreach
        data={outreach}
        loading={loading.outreach}
        onCreateTask={handleOutreachCreateTask}
        helpContent={CM_HELP_CONTENT.outreach}
      />

      {/* Panel 6: Polypharmacy */}
      <PanelPolypharmacy
        data={polypharmacy}
        loading={loading.polypharmacy}
        onCreateTask={handlePolypharmacyCreateTask}
        helpContent={CM_HELP_CONTENT.polypharmacy}
      />

      {/* Panel 7: Tasks */}
      <PanelCMTasks
        tasks={tasks}
        stats={taskStats}
        loading={loading.overview}
        onComplete={(id) => completeTask(id, currentUser?.employeeId || currentUser?.employee)}
        onCancel={(id) => updateTask(id, 29, currentUser?.employeeId || currentUser?.employee)}
        onDelete={(id) => deleteTask(id, currentUser?.employeeId || currentUser?.employee)}
        onEdit={(task) => openTaskModal(task)}
        onNewTask={() => openTaskModal()}
        helpContent={CM_HELP_CONTENT.cmTasks}
      />

      {/* Task Modal */}
      <TaskModal
        open={taskModalOpen}
        onClose={() => setTaskModalOpen(false)}
        onSubmit={handleCreateTask}
        defaultValues={taskModalDefaults}
        patients={patientList}
        employees={employees}
        currentUser={currentUser}
      />
    </Stack>
  );
}
